import { v4 as uuidv4 } from 'uuid';

import { ChatSession } from '../App';
import { ChatMessageGroup } from '../components/MessageGroup';
import { PageUpdateMessageContent } from '../components/message-types/PageUpdateMessage';
import { LIVECHAT_CLOSING_QUESTION_INTENT, isReadOnlyMode } from '../globals';
import ChatService, { ChatHistorySession } from '../services/ChatService';
import {
	ActivateLiveChatChatMessage,
	ChatHistoryType,
	ChatMessage,
	ChatMessageTypes,
	ChatMode,
	LiveChatAgent,
	LiveChatAgentStateChangeChatMessage,
	LiveChatAgentStateChangeMessageContentType,
	LiveChatStartChatMessage,
	LiveChatStatus,
	PageUpdateChatMessage,
	SenderTypes,
	SimpleChatMessage,
} from '../store';
import { SessionCookie, getSessionId } from './storage';

let ChatServiceInstance: ChatService;

// TODO: add test
export function getMessageFromMessageGroupByMessageId(
	messageId: string,
	messageGroup: ChatMessageGroup
): undefined | ChatMessage {
	if (Array.isArray(messageGroup.messages) && messageGroup.messages.length) {
		return messageGroup.messages.find((message) => message.id === messageId);
	}

	return undefined;
}

export function getMessageGroupByMessageId(
	messageId: string,
	chatHistory: ChatHistoryType
): undefined | ChatMessageGroup {
	if (Array.isArray(chatHistory) && chatHistory.length) {
		return chatHistory.find((group) => {
			return group.messages.find((message) => message.id === messageId)
				? true
				: false;
		});
	}

	return undefined;
}

export function getLastMessageGroupContainingAnIntent(
	chatHistory: ChatHistoryType
): undefined | ChatMessageGroup {
	if (Array.isArray(chatHistory) && chatHistory.length) {
		const chatHistoryCopy = [...chatHistory];

		return chatHistoryCopy
			.reverse()
			.find(
				(messageGroup) => messageGroup.intent && messageGroup.intent.length > 0
			);
	}

	return undefined;
}

export function getLastSentMessageFromHistory(
	history: ChatHistoryType
): ChatMessage | null {
	const lastUserMessageGroups = history.filter(
		(messageGroup) =>
			messageGroup.sender === SenderTypes.System ||
			messageGroup.sender === SenderTypes.User
	);
	const lastUserMessageGroup =
		lastUserMessageGroups[lastUserMessageGroups.length - 1];

	if (lastUserMessageGroup) {
		return lastUserMessageGroup.messages[
			lastUserMessageGroup.messages.length - 1
		];
	}
	return null;
}

// TODO: Improve naming convention (ofType vs WithType)
export function getGroupIndexOfLastMessageWithTypeFromChatHistory(
	history: ChatHistoryType,
	type: ChatMessageTypes
): number {
	if (Array.isArray(history) && history.length) {
		for (let grpIdx = history.length - 1; grpIdx >= 0; grpIdx--) {
			const currentGroup = history[grpIdx];
			for (
				let msgIdx = currentGroup.messages.length - 1;
				msgIdx >= 0;
				msgIdx--
			) {
				const currentMessage = currentGroup.messages[msgIdx];
				if (currentMessage.type === type) {
					return grpIdx;
				}
			}
		}
	}
	return -1;
}

export function getMessageGroupsContainingMessagesOfType(
	messageGroups: ChatHistoryType,
	messageType: ChatMessageTypes
): ChatHistoryType {
	if (Array.isArray(messageGroups) && messageGroups.length) {
		return messageGroups.filter((group) => {
			const { messages } = group;
			if (messages.length) {
				return messages.find((message) => message.type === messageType)
					? true
					: false;
			}
			return false;
		});
	}
	return [];
}

// Set up whether we need to show the avatar, timestamp or not render the group at all
// A = Avatar
// M = Message
// T = Timestamp

// {user: [textMessage]} M,T
// {chatbot: [activateLiveChatResponse]} DO NOT RENEDER
// {livechat: [startLiveChatResponse]} DO NOT RENEDER
// {livechat: [agent state change]} - M
// {livechat: [textMessage]} A,M,T
// {chatbot: [textMessage]} A,M,T
export function updateVisualCues(groups: ChatHistoryType): ChatHistoryType {
	const grps = [...groups];

	const messageTypesWhichShouldBeRenderedWithAvatarAndTimestamp = [
		ChatMessageTypes.Simple,
		ChatMessageTypes.Button,
		ChatMessageTypes.RecommendedProducts,
		ChatMessageTypes.LinkButton,
		ChatMessageTypes.AddToBasket,
		ChatMessageTypes.Tip,
		ChatMessageTypes.Image,
		ChatMessageTypes.Video,
		ChatMessageTypes.SuggestedPhrases,
		ChatMessageTypes.TrackedDelivery,
		ChatMessageTypes.ProductCard,
	];

	for (let i = 0; i < grps.length; i++) {
		const currentGroup = grps[i];

		currentGroup.visualCues = {
			isRendered: true,
			hasAvatar: true,
			hasTimestamp: true,
		};

		switch (currentGroup.sender) {
			case SenderTypes.Chatbot: {
				const messageTypes = currentGroup.messages.map(
					(message) => message.type
				);

				if (
					messageTypes.includes(ChatMessageTypes.ActivateLiveChat) &&
					!messageTypes.some((type) =>
						messageTypesWhichShouldBeRenderedWithAvatarAndTimestamp.includes(
							type
						)
					)
				) {
					currentGroup.visualCues = {
						isRendered: false,
						hasAvatar: false,
						hasTimestamp: false,
					};
				}
				break;
			}
			case SenderTypes.User: {
				currentGroup.visualCues.hasAvatar = false;
				const messageTypes = currentGroup.messages.map(
					(message) => message.type
				);
				currentGroup.visualCues.hasTimestamp =
					messageTypes.indexOf(ChatMessageTypes.LiveChatExit) > -1
						? false
						: true;
				break;
			}
			case SenderTypes.System: {
				const messageTypes = currentGroup.messages.map(
					(message) => message.type
				);
				const isRendered =
					messageTypes.includes(ChatMessageTypes.PageUpdate) && isReadOnlyMode
						? true
						: false;
				currentGroup.visualCues = {
					isRendered,
					hasAvatar: false,
					hasTimestamp: false,
				};
				break;
			}
			case SenderTypes.LiveChat:
				// TODO: Create a matrix of override rules???
				const textMessage = currentGroup.messages.find(
					(message) => message.type === ChatMessageTypes.Simple
				);

				if (textMessage) {
					continue;
				}

				const agentStateChangeMessage = currentGroup.messages.find(
					(message) =>
						message.type === ChatMessageTypes.LiveChatAgentStateChange ||
						message.type === ChatMessageTypes.LiveChatAgentTransferInitiate ||
						message.type === ChatMessageTypes.LiveChatAutoDisconnectNotice ||
						message.type === ChatMessageTypes.LiveChatAutoDisconnection
				);
				if (agentStateChangeMessage) {
					currentGroup.visualCues = {
						isRendered: true,
						hasAvatar: false,
						hasTimestamp: false,
					};
					continue;
				}

				const hiddenMessagesExist = currentGroup.messages.find(
					(message) =>
						message.type === ChatMessageTypes.LiveChatStart ||
						message.type === ChatMessageTypes.LiveChatTypingIndicator
				);
				if (hiddenMessagesExist) {
					currentGroup.visualCues = {
						isRendered: false,
						hasAvatar: false,
						hasTimestamp: false,
					};
					continue;
				}

				break;
			default:
				break;
		}
	}

	return grps;
}

// For example splitMessagesOfTypeIntoSeparateGroups(history, ChatMessageTypes.LiveChatAgentStateChange)
// [agentConnect, agentTransferInitiated, agentDisconnect]
// [agentConnect], [agentTransferInitiated], [agentDisconnect]
export function splitMessagesOfTypeIntoSeparateGroups(
	history: ChatHistoryType,
	typeToSplit: ChatMessageTypes
): ChatHistoryType {
	return history.reduce(
		(groupAcc: ChatHistoryType, group: ChatMessageGroup) => {
			const grp = { ...group };
			const groups: ChatHistoryType = [];
			const groupContainsTargetMessageType = grp.messages.find(
				(m) => m.type === typeToSplit
			)
				? true
				: false;

			if (groupContainsTargetMessageType) {
				for (let i = 0; i < group.messages.length; i++) {
					let createNewGroup = false;
					const currentMessage = group.messages[i];
					const prevMessage = group.messages[i - 1];

					if (
						typeToSplit === ChatMessageTypes.LiveChatAgentStateChange &&
						currentMessage.type === ChatMessageTypes.LiveChatAgentStateChange
					) {
						// TODO: Move into a separate function to add live chat agent details from message to parent group of message
						grp.liveChatAgent = {
							name: (currentMessage as LiveChatAgentStateChangeChatMessage)
								.content.name,
							initials: (currentMessage as LiveChatAgentStateChangeChatMessage)
								.content.initials,
							participantId: (
								currentMessage as LiveChatAgentStateChangeChatMessage
							).content.participantId,
							isConnected: false,
						};
					}

					if (prevMessage) {
						if (currentMessage.type === typeToSplit) {
							createNewGroup = prevMessage.type !== typeToSplit;
						} else {
							createNewGroup = prevMessage.type === typeToSplit;
						}
					}

					if (createNewGroup) {
						groups.push({
							...grp,
							messages: [currentMessage],
							timestamp: grp.timestamp,
						});
					} else {
						let currentGroup = groups.length
							? { ...groups[groups.length - 1] }
							: { ...grp };

						const messages = groups.length
							? [...currentGroup.messages, currentMessage]
							: [currentMessage];

						currentGroup = {
							...currentGroup,
							messages,
						};

						if (groups.length) {
							groups[groups.length - 1] = currentGroup;
						} else {
							groups.push(currentGroup);
						}
					}
				}
				return [...groupAcc, ...groups];
			}
			return [...groupAcc, grp] as ChatHistoryType;
		},
		[]
	);
}

export function getMessageGroupsOfSenderType(
	messageGroups: ChatHistoryType,
	senderType: SenderTypes
): ChatHistoryType {
	if (Array.isArray(messageGroups) && messageGroups.length) {
		return messageGroups.filter((group) => group.sender === senderType);
	}
	return [];
}

export function getLastMessageFromGroup(
	group: ChatMessageGroup
): ChatMessage | null {
	return group.messages[group.messages.length - 1];
}

export function getLastMessageOfTypeFromChatHistory(
	history: ChatHistoryType,
	type: ChatMessageTypes
): ChatMessage | undefined {
	if (Array.isArray(history) && history.length) {
		for (let i = history.length - 1; i >= 0; i--) {
			const currentGroup = history[i];
			for (let j = currentGroup.messages.length - 1; j >= 0; j--) {
				const currentMessage = currentGroup.messages[j];
				if (currentMessage.type === type) {
					return currentMessage;
				}
			}
		}
	}

	return undefined;
}

export function getLastConnectedAgentFromHistory(
	history: ChatHistoryType
): LiveChatAgent | null {
	const lastAgentStateChange = getLastMessageOfTypeFromChatHistory(
		history,
		ChatMessageTypes.LiveChatAgentStateChange
	) as LiveChatAgentStateChangeChatMessage;

	if (lastAgentStateChange) {
		const {
			content: { name, initials, participantId, state },
		} = lastAgentStateChange;
		return {
			name: name,
			initials: initials,
			participantId: participantId,
			isConnected: state === 'active',
		};
	}
	return null;
}

// TODO: Unit test
export function getLastPageUpdateFromChatHistory(
	chatHistory: ChatHistoryType
): undefined | PageUpdateMessageContent {
	const lastPageUpdateMessage = getLastMessageOfTypeFromChatHistory(
		chatHistory,
		ChatMessageTypes.PageUpdate
	) as PageUpdateChatMessage;

	return lastPageUpdateMessage ? lastPageUpdateMessage.content : undefined;
}

export function removeMessagesOfType(
	groups: ChatHistoryType,
	type: ChatMessageTypes
): ChatHistoryType {
	const grps = [...groups];
	for (let i = 0; i < grps.length; i++) {
		const currentGroup = grps[i];
		grps[i].messages = currentGroup.messages.filter(
			(message) => message.type !== type
		);
	}

	return grps.filter((g) => g.messages.length > 0);
}

// Remove any binary choice button messages, which have already been used or belong to older message groups
export function removeExpiredButtons(groups: ChatHistoryType): ChatHistoryType {
	return [...groups]
		.reverse() // Loop over from last to first message group
		.map((currentGroup, index) => {
			if (currentGroup.sender === SenderTypes.Chatbot) {
				if (index === 0) {
					return currentGroup;
				}
				return {
					...currentGroup,
					messages: currentGroup.messages.filter((message) =>
						message.type === ChatMessageTypes.Button &&
						message.content.length === 2
							? false
							: true
					),
				};
			}
			return currentGroup;
		})
		.reverse() // Revert the order
		.filter((g) => g.messages.length > 0);
}

export function disableExpiredAltPathButtonsWithinAddToBasketMessages(
	groups: ChatHistoryType
): ChatHistoryType {
	if (Array.isArray(groups) && groups.length) {
		const groupCount = groups.length;

		return groups.map((currentGroup, groupIdx) => {
			const isLastGroup = groupCount - 1 === groupIdx;
			const messageCount = currentGroup.messages.length;

			return {
				...currentGroup,
				messages: currentGroup.messages.map((message, messageIdx) => {
					if (isLastGroup && messageCount - 1 === messageIdx) {
						return message;
					}
					if (message.type === ChatMessageTypes.AddToBasket) {
						return {
							...message,
							content: { ...message.content, enableAltPath: false },
						};
					}
					return message;
				}),
			};
		});
	}
	return groups;
}

export function adjustAllLiveChatGroupsToReflectTheCurrentConnectionState(
	groups: ChatHistoryType
): ChatHistoryType {
	const grps = [...groups];

	const indexOfLastGroupContainingAnAutoDisconnection =
		getGroupIndexOfLastMessageWithTypeFromChatHistory(
			groups,
			ChatMessageTypes.LiveChatAutoDisconnection
		);
	const indexOfLastGroupContainingAUserDisconnection =
		getGroupIndexOfLastMessageWithTypeFromChatHistory(
			groups,
			ChatMessageTypes.LiveChatExit
		);
	const indexOfLastGroupContainingAnAgentStatusChange =
		getGroupIndexOfLastMessageWithTypeFromChatHistory(
			groups,
			ChatMessageTypes.LiveChatAgentStateChange
		);
	const lastAgentStateChange = getLastMessageOfTypeFromChatHistory(
		groups,
		ChatMessageTypes.LiveChatAgentStateChange
	) as LiveChatAgentStateChangeChatMessage;

	// Locate the last group with a live chat agent state change event
	// and set the live chat agent connection status accordingly
	if (lastAgentStateChange) {
		const lastGroupContainingAnAgentStatusChange =
			grps[indexOfLastGroupContainingAnAgentStatusChange];
		const { state } = lastAgentStateChange.content;
		let isConnected = state === 'active';
		if (
			indexOfLastGroupContainingAnAutoDisconnection >
			indexOfLastGroupContainingAnAgentStatusChange
		) {
			// The last conversaton was auto disconnected before the agent or user could manually disconnect,
			// so they must not be connected anymore
			isConnected = false;
		}

		if (
			indexOfLastGroupContainingAUserDisconnection >
			indexOfLastGroupContainingAnAgentStatusChange
		) {
			// The user has disconnected after the agent disconnected
			isConnected = false;
		}

		if (lastGroupContainingAnAgentStatusChange.liveChatAgent) {
			lastGroupContainingAnAgentStatusChange.liveChatAgent.isConnected =
				isConnected;
		}
	}

	// Propagate the last agent state to all subsequent livechat groups ..
	if (indexOfLastGroupContainingAnAgentStatusChange > -1) {
		const lastLiveChatAgent = grps[
			indexOfLastGroupContainingAnAgentStatusChange
		].liveChatAgent as LiveChatAgent;

		for (
			let i = indexOfLastGroupContainingAnAgentStatusChange + 1;
			i < grps.length;
			i++
		) {
			const currentGroup = grps[i];

			if (currentGroup.sender === SenderTypes.LiveChat) {
				if (currentGroup.liveChatAgent) {
					currentGroup.liveChatAgent.isConnected =
						lastLiveChatAgent.isConnected;
				} else {
					currentGroup.liveChatAgent = {
						...lastLiveChatAgent,
					};
				}
			}
		}
	}

	// Ensure all preceding livechat groups have a liveChatAgent populated ..
	for (let i = indexOfLastGroupContainingAnAgentStatusChange - 1; i > -1; i--) {
		const currentGroup = grps[i];
		if (
			currentGroup.sender === SenderTypes.LiveChat &&
			!currentGroup.liveChatAgent
		) {
			for (let j = i - 1; j > -1; j--) {
				const precedingGroup = grps[j];
				if (
					precedingGroup.sender === SenderTypes.LiveChat &&
					precedingGroup.liveChatAgent
				) {
					currentGroup.liveChatAgent = {
						...precedingGroup.liveChatAgent,
					} as LiveChatAgent;
					break;
				}
			}
		}
	}

	return grps;
}

// [liveChat, user, user]
export function mergeConsecutiveMessageGroupsOfSenderType(
	groups: ChatHistoryType,
	sender: SenderTypes
): ChatHistoryType {
	const newGroups: ChatHistoryType = [];

	for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
		const prevGroupIndex = newGroups.length - 1;
		const prevGroup = newGroups.length ? newGroups[prevGroupIndex] : null;
		const currentGroup = groups[groupIndex];
		if (
			currentGroup.sender === sender &&
			prevGroupIndex >= 0 &&
			prevGroup &&
			prevGroup.sender === currentGroup.sender
		) {
			newGroups[prevGroupIndex] = {
				...prevGroup,
				messages: [...prevGroup.messages, ...currentGroup.messages],
			};
		} else {
			newGroups.push(currentGroup);
		}
	}

	return newGroups;
}

export function mergeConsecutiveTextMessagesSentFromTheSameLiveChatAgent(
	groups: ChatHistoryType
): ChatHistoryType {
	let newGroups: ChatHistoryType = [];

	for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
		const currentGroup = groups[groupIndex];
		if (currentGroup.sender === SenderTypes.LiveChat) {
			const currentMessage = currentGroup.messages[0];
			if (currentMessage.type === ChatMessageTypes.Simple) {
				const prevGroup = newGroups[newGroups.length - 1];
				if (
					prevGroup &&
					prevGroup.sender === SenderTypes.LiveChat &&
					prevGroup.liveChatAgent &&
					currentGroup.liveChatAgent &&
					prevGroup.liveChatAgent.participantId ===
						currentGroup.liveChatAgent.participantId
				) {
					if (
						prevGroup.messages[prevGroup.messages.length - 1].type ===
						ChatMessageTypes.Simple
					) {
						prevGroup.messages = [
							...prevGroup.messages,
							...currentGroup.messages,
						] as ChatMessage[];
						continue;
					}
				}
			}
		}
		newGroups = [...newGroups, currentGroup];
	}
	return newGroups;
}

export function getGroupIndexOfLastMessageWithTextFromChatHistory(
	history: ChatHistoryType,
	text: string
): number {
	if (Array.isArray(history) && history.length) {
		for (let grpIdx = history.length - 1; grpIdx >= 0; grpIdx--) {
			const currentGroup = history[grpIdx];
			for (
				let msgIdx = currentGroup.messages.length - 1;
				msgIdx >= 0;
				msgIdx--
			) {
				const currentMessage = currentGroup.messages[msgIdx];
				if (currentMessage.type === ChatMessageTypes.Simple) {
					const simpleMessage = currentMessage as SimpleChatMessage;
					if (simpleMessage.content === text) {
						return grpIdx;
					}
				}
			}
		}
	}

	return -1;
}

export function getLiveChatStatusFromHistoryViewModel(
	history: ChatHistoryType
): LiveChatStatus {
	if (!Array.isArray(history) || history.length === 0) {
		return LiveChatStatus.Disconnected;
	}

	const startGroupIndex = getGroupIndexOfLastMessageWithTypeFromChatHistory(
		history,
		ChatMessageTypes.LiveChatStart
	);
	const agentStateChangeGroupIndex =
		getGroupIndexOfLastMessageWithTypeFromChatHistory(
			history,
			ChatMessageTypes.LiveChatAgentStateChange
		);
	const agentTransferInitiateGroupIndex =
		getGroupIndexOfLastMessageWithTypeFromChatHistory(
			history,
			ChatMessageTypes.LiveChatAgentTransferInitiate
		);
	const activateLiveChatGroupIndex =
		getGroupIndexOfLastMessageWithTypeFromChatHistory(
			history,
			ChatMessageTypes.ActivateLiveChat
		);
	const userDisconnectedLiveChatGroupIndex =
		getGroupIndexOfLastMessageWithTypeFromChatHistory(
			history,
			ChatMessageTypes.LiveChatExit
		);
	const autoDisconnectionMessageGroupIndex =
		getGroupIndexOfLastMessageWithTypeFromChatHistory(
			history,
			ChatMessageTypes.LiveChatAutoDisconnection
		);

	if (activateLiveChatGroupIndex === history.length - 1) {
		return LiveChatStatus.ConnectionError;
	}

	if (
		userDisconnectedLiveChatGroupIndex >
		Math.max(
			startGroupIndex,
			agentStateChangeGroupIndex,
			agentTransferInitiateGroupIndex
		)
	) {
		return LiveChatStatus.Disconnected;
	}

	if (
		autoDisconnectionMessageGroupIndex >
		Math.max(
			startGroupIndex,
			agentStateChangeGroupIndex,
			agentTransferInitiateGroupIndex
		)
	) {
		return LiveChatStatus.Disconnected;
	}

	if (
		startGroupIndex >
		Math.max(agentStateChangeGroupIndex, agentTransferInitiateGroupIndex)
	) {
		return LiveChatStatus.Connecting;
	}

	if (agentTransferInitiateGroupIndex > agentStateChangeGroupIndex) {
		return LiveChatStatus.Transferring;
	}

	const agentStateChange = getLastMessageOfTypeFromChatHistory(
		history,
		ChatMessageTypes.LiveChatAgentStateChange
	) as LiveChatAgentStateChangeChatMessage;

	if (agentStateChange) {
		if (agentStateChange.content.state === 'active') {
			return LiveChatStatus.Connected;
		}

		if (agentTransferInitiateGroupIndex === agentStateChangeGroupIndex - 1) {
			return LiveChatStatus.Transferring;
		}

		return LiveChatStatus.Disconnected;
	}

	return LiveChatStatus.Disconnected;
}

export function mapToHistoryViewModel(
	history: ChatHistoryType
): ChatHistoryType {
	if (!Array.isArray(history)) {
		return [];
	}
	if (!history.length) {
		return [];
	}

	// TODO: Create "pluckMessagesOfTypeIntoSeparateGroup" method instead of splitMessagesOfTypeIntoSeparateGroups
	// so we can pluck multiple message types into a new group
	let grps = removeMessagesOfType(
		[...history],
		ChatMessageTypes.LiveChatTypingIndicator
	);
	grps = splitMessagesOfTypeIntoSeparateGroups(
		grps,
		ChatMessageTypes.LiveChatAgentStateChange
	);
	grps = splitMessagesOfTypeIntoSeparateGroups(
		grps,
		ChatMessageTypes.LiveChatAgentTransferInitiate
	);
	grps = splitMessagesOfTypeIntoSeparateGroups(
		grps,
		ChatMessageTypes.LiveChatAutoDisconnectNotice
	);
	grps = splitMessagesOfTypeIntoSeparateGroups(
		grps,
		ChatMessageTypes.LiveChatAutoDisconnection
	);
	grps = removeExpiredButtons(grps);
	grps = disableExpiredAltPathButtonsWithinAddToBasketMessages(grps);
	grps = adjustAllLiveChatGroupsToReflectTheCurrentConnectionState(grps);
	grps = mergeConsecutiveMessageGroupsOfSenderType(grps, SenderTypes.User);
	grps = mergeConsecutiveTextMessagesSentFromTheSameLiveChatAgent(grps);
	grps = splitMessagesOfTypeIntoSeparateGroups(
		grps,
		ChatMessageTypes.LiveChatExit
	);
	grps = updateVisualCues(grps);

	return grps;
}

export function mergeRemoteAndLocalState(
	localStoredSession?: Partial<ChatSession>
): Promise<ChatSession> {
	const sessionId = getSessionId();

	if (sessionId) {
		if (!ChatServiceInstance) {
			ChatServiceInstance = new ChatService();
		}

		return new Promise(async (resolve) => {
			let response: ChatHistorySession | null = null;
			try {
				response = await ChatServiceInstance.getRemoteSessionState(sessionId);
			} catch (error) {
				// console.log('getChatSession -> Error:', error);
			}

			if (response) {
				const { history: rawHistory, results, id: newSessionId } = response;
				const historyViewModel = mapToHistoryViewModel(rawHistory);
				const liveChatStatus =
					getLiveChatStatusFromHistoryViewModel(historyViewModel);

				const activateLiveChatResponse = getLastMessageOfTypeFromChatHistory(
					historyViewModel,
					ChatMessageTypes.ActivateLiveChat
				) as ActivateLiveChatChatMessage;

				let currentChatMode = ChatMode.ChatBot;
				const currentChatInitialised = historyViewModel.length ? true : false;
				const currentLiveChatActivationData = activateLiveChatResponse
					? activateLiveChatResponse.content
					: undefined;
				let currentLiveChatStatus: LiveChatStatus | undefined = liveChatStatus;
				let currentLiveChatAgent;

				if (
					liveChatStatus === LiveChatStatus.Connecting ||
					liveChatStatus === LiveChatStatus.Connected ||
					liveChatStatus === LiveChatStatus.Transferring
				) {
					const liveChatStartResponse = getLastMessageOfTypeFromChatHistory(
						historyViewModel,
						ChatMessageTypes.LiveChatStart
					) as LiveChatStartChatMessage;

					if (liveChatStartResponse) {
						currentChatMode = ChatMode.LiveChat;
						currentLiveChatStatus = liveChatStatus;
						if (liveChatStatus === LiveChatStatus.Connected) {
							const agent = getLastConnectedAgentFromHistory(historyViewModel);
							if (agent) {
								currentLiveChatAgent = agent;
							}
						}
					}
				} else if (liveChatStatus === LiveChatStatus.ConnectionError) {
					currentChatMode = ChatMode.LiveChat;
					currentLiveChatStatus = liveChatStatus;
				} else if (liveChatStatus !== LiveChatStatus.Disconnected) {
					currentLiveChatStatus = undefined;
				}

				return resolve({
					...localStoredSession,
					sessionId: newSessionId,
					chatInitialised: currentChatInitialised,
					chatMode: currentChatMode,
					chatHistory: historyViewModel,
					liveChatStatus: currentLiveChatStatus,
					liveChatAgent: currentLiveChatAgent,
					liveChatActivationData: currentLiveChatActivationData,
					results,
					lastPageUpdate: getLastPageUpdateFromChatHistory(historyViewModel),
				});
			}

			return resolve({ sessionId, ...localStoredSession } as ChatSession);
		});
	}

	return Promise.resolve({ ...localStoredSession } as ChatSession);
}

export function getGroupIndexOfLastAgentStateChangeMessageFromChatHistory(
	history: ChatHistoryType,
	stateValue: string
): number {
	if (Array.isArray(history) && history.length) {
		for (let grpIdx = history.length - 1; grpIdx >= 0; grpIdx--) {
			const currentGroup = history[grpIdx];
			for (
				let msgIdx = currentGroup.messages.length - 1;
				msgIdx >= 0;
				msgIdx--
			) {
				const currentMessage = currentGroup.messages[msgIdx];
				if (currentMessage.type === ChatMessageTypes.LiveChatAgentStateChange) {
					if (
						(
							currentMessage.content as LiveChatAgentStateChangeMessageContentType
						).state === stateValue
					) {
						return grpIdx;
					}
				}
			}
		}
	}

	return -1;
}

export interface ConversationSummary {
	liveChatConnectingStartTime?: number;
	liveChatConnectedTime?: number;
	liveChatTransferringStartTime?: number;
	liveChatUserTextMessageCount: number;
	liveChatAgentTextMessageCount: number;
}

export function getConversationSummary(
	history: ChatHistoryType
): ConversationSummary {
	const summary = {
		liveChatUserTextMessageCount: 0,
		liveChatAgentTextMessageCount: 0,
	} as ConversationSummary;

	const lastActivateLiveChatGroupIndex =
		getGroupIndexOfLastMessageWithTypeFromChatHistory(
			history,
			ChatMessageTypes.ActivateLiveChat
		);

	if (lastActivateLiveChatGroupIndex === -1) {
		return summary;
	}

	summary.liveChatConnectingStartTime =
		history[lastActivateLiveChatGroupIndex].timestamp;

	for (let i = lastActivateLiveChatGroupIndex; i < history.length; i++) {
		const currentGroup = history[i];
		for (let j = 0; j < currentGroup.messages.length; j++) {
			const currentMessage = currentGroup.messages[j];
			if (currentMessage.type === ChatMessageTypes.LiveChatAgentStateChange) {
				const agentStateChangedMessage =
					currentMessage as LiveChatAgentStateChangeChatMessage;
				if (agentStateChangedMessage.content.state === 'active') {
					summary.liveChatConnectedTime = currentGroup.timestamp;
					break;
				}
			}
		}
		if (summary.liveChatConnectedTime) {
			break;
		}
	}

	const lastAgentTransferInitiateGroupIndex =
		getGroupIndexOfLastMessageWithTypeFromChatHistory(
			history,
			ChatMessageTypes.LiveChatAgentTransferInitiate
		);

	if (lastAgentTransferInitiateGroupIndex > -1) {
		summary.liveChatTransferringStartTime =
			history[lastAgentTransferInitiateGroupIndex].timestamp;
	}

	const lastAutoDisconnectionGroupIndex =
		getGroupIndexOfLastMessageWithTypeFromChatHistory(
			history,
			ChatMessageTypes.LiveChatAutoDisconnection
		);

	const lastDisconnectedAgentGroupIndex =
		getGroupIndexOfLastAgentStateChangeMessageFromChatHistory(
			history,
			'disconnected'
		);

	const conversationEndGroupIndex = Math.max(
		lastAutoDisconnectionGroupIndex,
		lastDisconnectedAgentGroupIndex
	);

	if (conversationEndGroupIndex === -1) {
		return summary;
	}

	const liveChatConversation = history.slice(
		lastActivateLiveChatGroupIndex,
		conversationEndGroupIndex
	);
	const allUserMesaageGroups = liveChatConversation.filter(
		(grp) => grp.sender === SenderTypes.User
	);
	const allAgentMesaageGroups = liveChatConversation.filter(
		(grp) => grp.sender === SenderTypes.LiveChat
	);

	summary.liveChatUserTextMessageCount = allUserMesaageGroups.reduce(
		(accum, currGrp) =>
			accum +
			currGrp.messages.filter((m) => m.type === ChatMessageTypes.Simple).length,
		0
	);
	summary.liveChatAgentTextMessageCount = allAgentMesaageGroups.reduce(
		(accum, currGrp) =>
			accum +
			currGrp.messages.filter((m) => m.type === ChatMessageTypes.Simple).length,
		0
	);

	return summary;
}
